// Transplanted exactly from App.js lines 418, 509-534, 2014-2028.
// REACT_APP_API_URL lets each deployment choose its API origin. Set to an
// empty string for same-origin proxies (e.g. path-based /api routing). When
// unset in the browser, we infer :3001 on the same host so phones never use
// their own localhost when you open VegAI via Tailscale or a LAN URL.
function resolveApiBaseUrl() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw === '') return '';
  if (raw != null && String(raw).trim() !== '') {
    return String(raw).trim().replace(/\/$/, '');
  }
  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:3001';
  }

  const { protocol, hostname, port } = window.location;
  const isLocal =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]';

  // CRA dev: frontend :3000, backend :3001
  if (isLocal && port === '3000') {
    return `${protocol}//${hostname}:3001`;
  }

  // HTTPS on a Tailscale MagicDNS host: use same-origin /api (Tailscale Serve on :443).
  // Otherwise the bundle falls through to hostname:3001, which often bypasses Serve and
  // breaks or behaves differently than https://host/api/... .
  if (!isLocal && protocol === 'https:' && /\.ts\.net$/i.test(hostname)) {
    return '';
  }

  // Phone / another machine: localhost in the bundle would target the wrong device
  if (!isLocal) {
    return `${protocol}//${hostname}:3001`;
  }

  return 'http://127.0.0.1:3001';
}

const API_BASE_URL = resolveApiBaseUrl();

// Conservative per-call timeouts so a hung backend can't leave the UI stuck on
// an indefinite spinner. The image-identify path gets a longer budget because
// OpenAI vision can legitimately take ~30s on a cold call.
const DEFAULT_TIMEOUT_MS = 30000;
const IMAGE_TIMEOUT_MS = 45000;
const SESSION_STORAGE_KEY = 'vegai.analytics.sessionId';

function getAnalyticsSessionId() {
  if (typeof window === 'undefined') return null;
  try {
    const existing = window.localStorage?.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const next = `anon_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage?.setItem(SESSION_STORAGE_KEY, next);
    return next;
  } catch {
    return null;
  }
}

function adminHeaders(secret) {
  const headers = { 'Content-Type': 'application/json' };
  const value = String(secret || '').trim();
  if (value) headers['x-vegai-admin-secret'] = value;
  return headers;
}

/**
 * Wrap `fetch` with an AbortController so every request has a hard deadline.
 * Throws an Error with `.code === 'TIMEOUT'` when the deadline trips so callers
 * can render a "Request timed out" message instead of spinning forever.
 */
async function fetchWithTimeout(url, init = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const sessionId = getAnalyticsSessionId();
  const headers = sessionId
    ? { ...(init.headers || {}), 'x-vegai-session-id': sessionId }
    : init.headers;
  try {
    return await fetch(url, { ...init, headers, signal: controller.signal });
  } catch (err) {
    if (err?.name === 'AbortError') {
      const e = new Error('Request timed out. Please check your connection and try again.');
      e.code = 'TIMEOUT';
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/** Parse JSON defensively so an HTML error page (proxy, 502, captive portal) doesn't throw. */
async function safeJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

/** Clears server-side lookup + analysis caches. Throws Error with short .code for UI mapping. */
export const clearServerCacheAPI = async () => {
  const headers = { 'Content-Type': 'application/json' };
  const secret = process.env.REACT_APP_CACHE_CLEAR_SECRET;
  if (secret) headers['x-vegai-cache-clear'] = secret;

  let response;
  try {
    response = await fetchWithTimeout(`${API_BASE_URL}/api/cache/clear`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    });
  } catch (err) {
    if (err?.code === 'TIMEOUT') throw err;
    const e = new Error('NETWORK');
    e.code = 'NETWORK';
    throw e;
  }

  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'CACHE_CLEAR_FORBIDDEN') {
      const err = new Error('CACHE_CLEAR_FORBIDDEN');
      err.code = 'CACHE_CLEAR_FORBIDDEN';
      throw err;
    }
    const err = new Error('REQUEST_FAILED');
    err.code = 'REQUEST_FAILED';
    throw err;
  }
  return data;
};

export const analyzeIngredientsAPI = async (
  ingredientsOrText,
  verifyMatch = undefined,
  productName = undefined,
  productMatch = undefined,
  sources = undefined,
  uiSource = undefined,
  resolvedVia = undefined,
  sourceEvidence = undefined,
  foodProfile = undefined
) => {
  try {
    const body = { ingredients: ingredientsOrText, source: 'manual' };
    if (verifyMatch !== undefined) body.verifyMatch = verifyMatch;
    if (productMatch !== undefined) body.productMatch = productMatch;
    if (sources !== undefined) body.sources = sources;
    if (resolvedVia !== undefined && resolvedVia !== null) body.resolvedVia = resolvedVia;
    if (sourceEvidence !== undefined && sourceEvidence !== null) body.sourceEvidence = sourceEvidence;
    if (foodProfile !== undefined && foodProfile !== null) body.foodProfile = foodProfile;

    const u = typeof uiSource === 'string' ? uiSource.toLowerCase().trim() : '';
    const label = productName != null && String(productName).trim() !== '' ? String(productName).trim() : '';
    if (u === 'scanner') {
      body.source = 'scanner';
      if (label) body.product = label;
    } else if (u === 'product') {
      body.source = 'product';
      if (label) body.product = label;
    } else if (label) {
      body.product = label;
      body.source = 'product';
    }
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    });
    const data = await safeJson(response);
    if (!response.ok) {
      if (data?.error === 'COST_LIMIT_REACHED' || data?.error === 'VEGAI_PAUSED') {
        throw new Error(data.message || 'VegAI is protecting the usage budget. Please try again a little later.');
      }
      if (data?.error === 'ANALYSIS_FAILED' || response.status >= 500) {
        throw new Error('Analysis failed on our end. Please try again in a moment.');
      }
      if (data?.error === 'NO_INGREDIENTS_FOUND') {
        throw new Error('Could not find ingredients for that product. Try a more specific name.');
      }
      if (data?.error === 'INGREDIENTS_REQUIRED') {
        throw new Error('Please provide an ingredient list.');
      }
      if (data?.error === 'PRODUCT_SELECTION_REQUIRED') {
        throw new Error('Please choose the exact product version before analyzing.');
      }
      if (data?.error === 'EXACT_PRODUCT_SOURCE_NOT_FOUND') {
        throw new Error('I found similar products, but not enough evidence for this exact one yet. Try a barcode, ingredient-label photo, or a more specific name.');
      }
      throw new Error(data?.message || 'Analysis failed. Please try again.');
    }
    return data;
  } catch (error) {
    console.error('API Error in analyzeIngredientsAPI:', error);
    throw error;
  }
};

export const getIngredientsByImageAPI = async (base64Image, mode) => {
  try {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/image-identify`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: base64Image, mode })
      },
      IMAGE_TIMEOUT_MS
    );
    const data = await safeJson(response);
    if (!response.ok) {
      if (data?.error === 'COST_LIMIT_REACHED' || data?.error === 'VEGAI_PAUSED') {
        throw new Error(data.message || 'VegAI is protecting the usage budget. Please try again a little later.');
      }
      if (data?.error === 'PRODUCT_NOT_IDENTIFIED') {
        throw new Error('Could not identify the product from the photo. Try a clearer image.');
      }
      if (data?.error === 'NO_INGREDIENTS_FOUND') {
        throw new Error(data.message || 'Could not read ingredients from the photo. Try a clearer image.');
      }
      if (
        data?.error === 'IMAGE_SERVICE_UNAVAILABLE' ||
        data?.error === 'IMAGE_MODEL_UNAVAILABLE'
      ) {
        throw new Error(data.message || 'Photo recognition needs a backend configuration update.');
      }
      if (data?.error === 'IMAGE_OPENAI_RATE_LIMITED') {
        throw new Error(data.message || 'Photo recognition is rate-limited right now. Please try again in a moment.');
      }
      if (response.status >= 500) {
        throw new Error('Photo recognition is temporarily unavailable. Please try again in a moment.');
      }
      throw new Error(data.message || 'Image processing failed. Please try again.');
    }

    return {
      ingredients: Array.isArray(data.ingredients) ? data.ingredients : [],
      verifyMatch: data.verifyMatch,
      productMatch: data.productMatch,
      variants: Array.isArray(data.variants) ? data.variants : [],
      brands: Array.isArray(data.brands) ? data.brands : [],
      sources: Array.isArray(data.sources) ? data.sources : [],
      resolvedVia: data.resolvedVia || null,
      sourceEvidence: data.sourceEvidence || null,
    };
  } catch (error) {
    console.error('API Error in getIngredientsByImageAPI:', error);
    throw error;
  }
};

export const getIngredientsByBarcodeAPI = async (barcode) => {
  const trimmed = String(barcode || '').trim();
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/get-ingredients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // `source` used to be sent here but the backend only reads it on /api/analyze;
      // keeping it would be dead data on the wire.
      body: JSON.stringify({ barcode: trimmed })
    });
    const data = await safeJson(response);
    if (!response.ok) {
      // Surface the server error code on the thrown Error so the scanner
      // panel can branch on INVALID_BARCODE, BARCODE_NOT_FOUND, and generic
      // failures without parsing the user-facing message string.
      if (data?.error === 'INVALID_BARCODE') {
        const err = new Error('That barcode number does not look valid. Please scan it again or type the full number from the package.');
        err.code = 'INVALID_BARCODE';
        err.barcode = data?.barcode || trimmed;
        err.reason = data?.reason || null;
        throw err;
      }
      if (data?.error === 'BARCODE_NOT_FOUND') {
        const err = new Error(`We couldn't find barcode ${trimmed} in our databases.`);
        err.code = 'BARCODE_NOT_FOUND';
        err.barcode = trimmed;
        throw err;
      }
      if (data?.error === 'BARCODE_INGREDIENTS_NOT_FOUND') {
        const product = String(data?.productMatch || '').trim();
        const err = new Error(
          product
            ? `We found ${product}, but trusted databases do not have ingredients for that barcode yet.`
            : `We found barcode ${trimmed}, but trusted databases do not have ingredients for it yet.`
        );
        err.code = 'BARCODE_INGREDIENTS_NOT_FOUND';
        err.barcode = data?.barcode || trimmed;
        err.productMatch = product || null;
        throw err;
      }
      if (data?.error === 'NO_INGREDIENTS_FOUND') {
        const err = new Error('Product not found for this barcode. Try scanning again or use manual entry.');
        err.code = 'NO_INGREDIENTS_FOUND';
        throw err;
      }
      const err = new Error('Barcode lookup is temporarily unavailable. Please try again in a moment.');
      err.code = data?.error || 'BARCODE_LOOKUP_FAILED';
      throw err;
    }

    if (Array.isArray(data.ingredients) && data.ingredients.length > 0) {
      return {
        ingredients: data.ingredients,
        verifyMatch: data.verifyMatch,
        productMatch: data.productMatch,
        sources: Array.isArray(data.sources) ? data.sources : [],
        resolvedVia: data.resolvedVia || null,
        sourceEvidence: data.sourceEvidence || null,
      };
    } else {
      const err = new Error(data.error || 'No ingredients found for this barcode');
      err.code = data?.error || 'NO_INGREDIENTS_FOUND';
      throw err;
    }
  } catch (error) {
    console.error('API Error in getIngredientsByBarcodeAPI:', error);
    throw error;
  }
};

/**
 * Product lookup: plain string = name search, or { offCode }, { fdcId }, { product } for explicit modes.
 */
export const getIngredientsAPI = async (lookup) => {
  try {
    let body = { source: 'product' };
    if (typeof lookup === 'string') {
      body.product = lookup.trim();
    } else if (lookup && typeof lookup === 'object') {
      if (lookup.offCode != null && String(lookup.offCode).trim()) {
        body = { source: 'product', offCode: String(lookup.offCode).trim() };
      } else if (lookup.fdcId != null && lookup.fdcId !== '') {
        body = { source: 'product', fdcId: Number(lookup.fdcId) };
      } else if (lookup.product != null && String(lookup.product).trim()) {
        body = { source: 'product', product: String(lookup.product).trim() };
      } else {
        throw new Error('Invalid product lookup.');
      }
    } else {
      throw new Error('Invalid product lookup.');
    }

    const response = await fetchWithTimeout(`${API_BASE_URL}/api/get-ingredients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await safeJson(response);
    if (!response.ok) {
      if (data?.error === 'NO_INGREDIENTS_FOUND') {
        throw new Error('Could not find ingredients for that product. Try a more specific product name.');
      }
      if (data?.error === 'EXACT_PRODUCT_SOURCE_NOT_FOUND') {
        const err = new Error('I found similar products, but not enough evidence for this exact one yet. Try a barcode, ingredient-label photo, or a more specific name.');
        err.code = 'EXACT_PRODUCT_SOURCE_NOT_FOUND';
        err.productMatch = data?.productMatch || null;
        err.sourceEvidence = data?.sourceEvidence || null;
        throw err;
      }
      if (data?.error === 'LOOKUP_REQUIRED') {
        throw new Error('Please enter a product name to search.');
      }
      if (data?.error === 'AMBIGUOUS_LOOKUP') {
        throw new Error('Please search by product name, barcode, or ID — not multiple at once.');
      }
      if (response.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }
      if (response.status >= 500) {
        throw new Error('Our server hit an issue. Please try again in a moment.');
      }
      throw new Error(data?.message || 'Product lookup failed. Please try again.');
    }

    const ingredients = Array.isArray(data.ingredients) ? data.ingredients : [];
    const brands = Array.isArray(data.brands) ? data.brands : [];
    const variants = Array.isArray(data.variants) ? data.variants : [];

    // Category results (e.g. "ramen") return empty ingredients but a list of
    // brands for the picker — propagate them so the frontend can render the
    // brand chips instead of surfacing a misleading "no ingredients" error.
    if (ingredients.length > 0 || brands.length > 0 || variants.length > 0) {
      return {
        ingredients,
        verifyMatch: data.verifyMatch,
        productMatch: data.productMatch,
        variants,
        brands,
        sources: Array.isArray(data.sources) ? data.sources : [],
        resolvedVia: data.resolvedVia || null,
        sourceEvidence: data.sourceEvidence || null,
      };
    }
    throw new Error(data.error || 'No ingredients found');
  } catch (error) {
    console.error('API Error in getIngredientsAPI:', error);
    throw error;
  }
};

export const getReviewQueueAPI = async ({ secret, status = 'open', limit = 50 } = {}) => {
  const qs = new URLSearchParams({ status, limit: String(limit) });
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/review-queue?${qs.toString()}`, {
    method: 'GET',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN') throw new Error('Admin secret is missing or incorrect.');
    throw new Error(data?.message || 'Could not load review queue.');
  }
  return Array.isArray(data.items) ? data.items : [];
};

export const listVerifiedCatalogAPI = async ({ secret, limit = 200, reviewStatus = 'all' } = {}) => {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (reviewStatus && reviewStatus !== 'all') qs.set('reviewStatus', reviewStatus);
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/verified-products?${qs.toString()}`, {
    method: 'GET',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN') throw new Error('Admin secret is missing or incorrect.');
    throw new Error(data?.message || 'Could not load verified catalog.');
  }
  return Array.isArray(data.items) ? data.items : [];
};

export const saveVerifiedProductAPI = async ({ secret, product }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/verified-products`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify(product || {}),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN') throw new Error('Admin secret is missing or incorrect.');
    throw new Error(data?.message || 'Could not save verified product.');
  }
  return data.verified;
};

export const deleteVerifiedProductAPI = async ({ secret, id }) => {
  const response = await fetchWithTimeout(
    `${API_BASE_URL}/api/verified-products/${encodeURIComponent(id)}/delete`,
    {
      method: 'POST',
      headers: adminHeaders(secret),
      body: JSON.stringify({}),
    }
  );
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN') throw new Error('Admin secret is missing or incorrect.');
    if (data?.error === 'VERIFIED_PRODUCT_NOT_FOUND') throw new Error('Verified product was already deleted.');
    throw new Error(data?.message || 'Could not delete verified product.');
  }
  return data.deleted;
};

export const restoreVerifiedProductAPI = async ({ secret, deleted }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/verified-products/restore`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ deleted }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN') throw new Error('Admin secret is missing or incorrect.');
    throw new Error(data?.message || 'Could not undo verified product deletion.');
  }
  return data.restored;
};

export const updateReviewQueueStatusAPI = async ({ secret, id, status, adminNotes }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/review-queue/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ status, adminNotes }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN') throw new Error('Admin secret is missing or incorrect.');
    throw new Error(data?.message || 'Could not update review item.');
  }
  return data.item || data;
};

export const updateReviewQueueCategoryAPI = async ({ secret, id, flagCategory }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/review-queue/${encodeURIComponent(id)}/category`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ flagCategory }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN') throw new Error('Admin secret is missing or incorrect.');
    throw new Error(data?.message || 'Could not save flag category.');
  }
  return data.item || data;
};

export const checkBackendHealthAPI = async ({ deep = true, openai = false } = {}) => {
  const path = deep ? '/health/deep' : '/health';
  const qs = openai ? '?openai=1' : '';
  const response = await fetchWithTimeout(`${API_BASE_URL}${path}${qs}`, { method: 'GET' });
  const data = await safeJson(response);
  if (!response.ok) {
    throw new Error(data?.message || data?.error || 'Backend health check failed.');
  }
  return data;
};

export const getCostControlsAPI = async ({ secret, limit = 25 } = {}) => {
  const qs = new URLSearchParams({ limit: String(limit) });
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/cost-controls?${qs.toString()}`, {
    method: 'GET',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    throw new Error(data?.message || 'Could not load cost controls.');
  }
  return data;
};

export const getAnalyticsSummaryAPI = async ({ secret, limit = 8 } = {}) => {
  const qs = new URLSearchParams({ limit: String(limit) });
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/analytics/summary?${qs.toString()}`, {
    method: 'GET',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    throw new Error(data?.message || 'Could not load analytics summary.');
  }
  return data;
};

export const trackAnalyticsEventAPI = ({ eventName, feature, metadata } = {}) => {
  if (process.env.NODE_ENV === 'test') return;
  const sessionId = getAnalyticsSessionId();
  const body = JSON.stringify({
    eventName,
    feature,
    metadata: {
      ...(metadata || {}),
      ...(sessionId ? { sessionId } : {}),
    },
  });
  const url = `${API_BASE_URL}/api/analytics/event`;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch (_) {
    /* fall through to fetch */
  }
  try {
    fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }, 5000).catch(() => {});
  } catch (_) {
    /* analytics must never block app usage */
  }
};

export const listOpsEventsAPI = async ({ secret, status = 'open', limit = 100 } = {}) => {
  const qs = new URLSearchParams({ status, limit: String(limit) });
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/ops-events?${qs.toString()}`, {
    method: 'GET',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    throw new Error(data?.message || 'Could not load operations events.');
  }
  return Array.isArray(data.items) ? data.items : [];
};

export const updateOpsEventStatusAPI = async ({ secret, id, status }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/ops-events/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ status }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    throw new Error(data?.message || 'Could not update operations event.');
  }
  return data.item || data;
};

export const sendBackendSentryTestAPI = async ({ secret } = {}) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/ops-events/sentry-test`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({}),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    throw new Error(data?.message || 'Could not send Sentry test event.');
  }
  return data;
};

export const getE2eResultsAPI = async ({ secret, limit = 10 } = {}) => {
  const qs = new URLSearchParams({ limit: String(limit) });
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/admin/e2e-results?${qs.toString()}`, {
    method: 'GET',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    throw new Error(data?.message || 'Could not load E2E testing results.');
  }
  return Array.isArray(data.items) ? data.items : [];
};

export const getE2eResultAPI = async ({ secret, id } = {}) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/admin/e2e-results/${encodeURIComponent(id)}`, {
    method: 'GET',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    if (data?.error === 'E2E_RUN_NOT_FOUND') {
      throw new Error('That E2E run could not be found.');
    }
    throw new Error(data?.message || 'Could not load that E2E run.');
  }
  return data.item || null;
};

export const deleteE2eResultAPI = async ({ secret, id } = {}) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/admin/e2e-results/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    if (data?.error === 'E2E_RUN_NOT_FOUND') {
      throw new Error('That E2E run could not be found.');
    }
    throw new Error(data?.message || 'Could not delete that E2E run.');
  }
  return data;
};

export const clearE2eResultsAPI = async ({ secret } = {}) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/admin/e2e-results`, {
    method: 'DELETE',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    throw new Error(data?.message || 'Could not clear E2E history.');
  }
  return data;
};

export const listFeedbackInboxAPI = async ({ secret, status = 'open', limit = 100 } = {}) => {
  const qs = new URLSearchParams({ status, limit: String(limit) });
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/feedback?${qs.toString()}`, {
    method: 'GET',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    throw new Error(data?.message || 'Could not load feedback inbox.');
  }
  return Array.isArray(data.items) ? data.items : [];
};

export const updateFeedbackStatusAPI = async ({ secret, id, status, adminNotes }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/feedback/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ status, adminNotes }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    throw new Error(data?.message || 'Could not update feedback item.');
  }
  return data.item;
};

export const applyFeedbackCorrectionAPI = async ({ secret, id, adminNotes }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/feedback/${encodeURIComponent(id)}/apply-correction`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ adminNotes }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    if (data?.error === 'ADMIN_FORBIDDEN' || data?.error === 'ADMIN_SECRET_NOT_CONFIGURED') {
      throw new Error('Admin secret is missing or incorrect.');
    }
    if (data?.error === 'NO_CORRECTION_TO_APPLY') {
      throw new Error('This feedback item does not include a correction to apply.');
    }
    throw new Error(data?.message || 'Could not apply correction.');
  }
  return data.item;
};

export const listLearningArchiveAPI = async ({ secret, archived = false, limit = 300 } = {}) => {
  const qs = new URLSearchParams({ archived: String(archived), limit: String(limit) });
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/learning-archive?${qs.toString()}`, {
    method: 'GET',
    headers: adminHeaders(secret),
  });
  const data = await safeJson(response);
  if (!response.ok) throw new Error(data?.message || 'Could not load the Learning Archive.');
  return Array.isArray(data.items) ? data.items : [];
};

export const saveLearningArchiveAPI = async ({ secret, entry }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/learning-archive`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify(entry),
  });
  const data = await safeJson(response);
  if (!response.ok) throw new Error(data?.message || 'Could not save this archive entry.');
  return data.item;
};

export const draftLearningArchiveAPI = async ({ secret, notes }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/learning-archive/draft`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ notes }),
  }, 45_000);
  const data = await safeJson(response);
  if (!response.ok) throw new Error(data?.message || 'Could not create an AI draft.');
  return data.draft;
};

export const archiveLearningArchiveAPI = async ({ secret, id, archived }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/learning-archive/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ archived }),
  });
  const data = await safeJson(response);
  if (!response.ok) throw new Error(data?.message || 'Could not update this archive entry.');
  return data.item;
};

export const deleteLearningArchiveAPI = async ({ secret, id }) => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/learning-archive/${encodeURIComponent(id)}/delete`, {
    method: 'POST',
    headers: adminHeaders(secret),
    body: JSON.stringify({ confirm: 'DELETE' }),
  });
  const data = await safeJson(response);
  if (!response.ok) throw new Error(data?.message || 'Could not permanently delete this archive entry.');
  return data.item;
};

/**
 * User-facing: ask admins to review this product/result (no admin secret).
 */
const cleanReviewText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    const clean = String(value).trim();
    return clean.toLowerCase() === '[object object]' ? '' : clean;
  }
  if (Array.isArray(value)) return value.map(cleanReviewText).filter(Boolean).join(' ').trim();
  if (typeof value === 'object') {
    return cleanReviewText(
      value.productMatch || value.productName || value.name || value.label || value.title || value.query || value.input || value.barcode
    );
  }
  return String(value).trim();
};

export const flagReviewQueueAPI = async ({ query, productMatch, reason, reasonDetail, result }) => {
  const body = { query: cleanReviewText(query) };
  if (!body.query) {
    const err = new Error('Missing product or search text to flag.');
    err.code = 'QUERY_REQUIRED';
    throw err;
  }
  const cleanProductMatch = cleanReviewText(productMatch);
  if (cleanProductMatch) body.productMatch = cleanProductMatch;
  if (reason != null && String(reason).trim()) body.reason = String(reason).trim().slice(0, 120);
  const cleanReasonDetail = cleanReviewText(reasonDetail).slice(0, 300);
  if (cleanReasonDetail) body.reasonDetail = cleanReasonDetail;
  if (result && typeof result === 'object') body.result = result;

  const response = await fetchWithTimeout(`${API_BASE_URL}/api/review-queue/flag`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    throw new Error(data?.message || data?.error || 'Could not flag result for review.');
  }
  if (data && data.queued === false) {
    throw new Error(
      data?.message || 'That could not be added to the review queue. Try a clearer product name.',
    );
  }
  return data;
};

/**
 * Submit in-app feedback (saved server-side as JSON lines under backend/data/).
 */
export const submitFeedbackAPI = async ({ category, message, contactEmail, context, correction }) => {
  let response;
  try {
    response = await fetchWithTimeout(`${API_BASE_URL}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        category,
        message,
        contactEmail,
        context,
        correction,
      }),
    });
  } catch (err) {
    if (err?.code === 'TIMEOUT') throw err;
    throw new Error('Network error. Check that the server is running and try again.');
  }
  const data = await safeJson(response);
  if (!response.ok) {
    throw new Error(data.message || 'Could not send feedback. Please try again.');
  }
  return data;
};

function accountHeaders(accessToken, includeJson = true) {
  const headers = {
    Authorization: `Bearer ${String(accessToken || '').trim()}`,
  };
  if (includeJson) headers['Content-Type'] = 'application/json';
  return headers;
}

async function accountRequest(path, { accessToken, method = 'GET', body } = {}) {
  let response;
  try {
    response = await fetchWithTimeout(`${API_BASE_URL}${path}`, {
      method,
      headers: accountHeaders(accessToken, body !== undefined),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (error) {
    if (error?.code === 'TIMEOUT') throw error;
    throw new Error(
      method === 'DELETE' && path === '/api/account'
        ? 'VegAI could not confirm account deletion. Refresh and try signing in again; if the account no longer exists, deletion succeeded.'
        : 'Could not reach VegAI to update your account. Check your connection and try again.'
    );
  }
  const data = await safeJson(response);
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Your sign-in session expired. Please sign in again.');
    }
    throw new Error(data?.message || 'Could not update your account.');
  }
  return data;
}

export const getAccountDataAPI = (accessToken) =>
  accountRequest('/api/account/data', { accessToken });

export const getFoodProfileAPI = (accessToken) =>
  accountRequest('/api/account/food-profile', { accessToken });

export const saveFoodProfileAPI = (accessToken, profile) =>
  accountRequest('/api/account/food-profile', {
    accessToken,
    method: 'PUT',
    body: { profile },
  });

export const importAccountLocalDataAPI = (accessToken, data) =>
  accountRequest('/api/account/import', {
    accessToken,
    method: 'POST',
    body: data,
  });

export const saveAccountFavoriteAPI = (accessToken, item) =>
  accountRequest('/api/account/favorites', {
    accessToken,
    method: 'POST',
    body: { item },
  });

export const deleteAccountFavoriteAPI = (accessToken, id) =>
  accountRequest(`/api/account/favorites/${encodeURIComponent(id)}`, {
    accessToken,
    method: 'DELETE',
  });

export const clearAccountFavoritesAPI = (accessToken) =>
  accountRequest('/api/account/favorites', {
    accessToken,
    method: 'DELETE',
  });

export const saveAccountHistoryAPI = (accessToken, item) =>
  accountRequest('/api/account/history', {
    accessToken,
    method: 'POST',
    body: { item },
  });

export const updateAccountHistoryTitleAPI = (accessToken, id, historyLabel) =>
  accountRequest(`/api/account/history/${encodeURIComponent(id)}`, {
    accessToken,
    method: 'PATCH',
    body: { historyLabel },
  });

export const deleteAccountHistoryAPI = (accessToken, id) =>
  accountRequest(`/api/account/history/${encodeURIComponent(id)}`, {
    accessToken,
    method: 'DELETE',
  });

export const clearAccountHistoryAPI = (accessToken) =>
  accountRequest('/api/account/history', {
    accessToken,
    method: 'DELETE',
  });

export const getAccountExportAPI = (accessToken) =>
  accountRequest('/api/account/export', { accessToken });

export const deleteAccountAPI = (accessToken) =>
  accountRequest('/api/account', {
    accessToken,
    method: 'DELETE',
    body: { confirmation: 'DELETE' },
  });
